import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Printer, ArrowLeft, Zap } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';

export default function Receipt() {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const isSample = transactionId === 'sample';

  useEffect(() => {
    const fetchReceipt = async () => {
      try {
        const endpoint = isSample ? '/receipt/sample' : `/transactions/${transactionId}/receipt`;
        const res = await api.get(endpoint);
        setData(res.data);
      } catch {
        navigate(isSample ? '/settings' : '/transactions');
      } finally {
        setLoading(false);
      }
    };
    fetchReceipt();
  }, [transactionId, navigate, isSample]);

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: (currency || 'eur').toUpperCase()
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-slate-200 border-t-orange-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const { transaction: txn, store } = data;

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8" data-testid="receipt-page">
      {/* Print-hidden controls */}
      <div className="max-w-md mx-auto mb-4 flex gap-2 print:hidden">
        <Button variant="outline" size="sm" onClick={() => navigate(isSample ? '/settings' : '/transactions')} data-testid="receipt-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> {isSample ? 'Back to Settings' : t('receipt.back')}
        </Button>
        {isSample && (
          <span className="inline-flex items-center px-3 py-1 text-xs font-bold uppercase tracking-wider bg-amber-100 text-amber-700 border border-amber-200">
            Sample Preview
          </span>
        )}
        {!isSample && (
          <Button size="sm" onClick={handlePrint} className="bg-slate-900 text-white" data-testid="receipt-print-btn">
            <Printer className="w-4 h-4 mr-1" /> {t('receipt.print')}
          </Button>
        )}
      </div>

      {/* Receipt Card */}
      <div className="max-w-md mx-auto bg-card border border-border shadow-sm" data-testid="receipt-card">
        {/* Header */}
        <div className="p-6 text-center border-b border-dashed border-border" style={{ backgroundColor: store?.brand_color || '#0F172A' }}>
          {store?.logo_url ? (
            <img
              src={store.logo_url.startsWith('http') ? store.logo_url : `${process.env.REACT_APP_BACKEND_URL}${store.logo_url}`}
              alt={store.name}
              className="h-10 mx-auto mb-2 object-contain"
            />
          ) : (
            <div className="flex items-center justify-center gap-2 mb-2">
              <Zap className="w-6 h-6 text-white" />
              <span className="text-lg font-extrabold tracking-tighter text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                EmergencyPOS
              </span>
            </div>
          )}
          <p className="text-sm font-semibold text-white">{store?.name}</p>
          {store?.address && <p className="text-xs text-white/70">{store.address}</p>}
          {store?.phone && <p className="text-xs text-white/70">{store.phone}</p>}
        </div>

        {/* Amount */}
        <div className="p-6 text-center">
          <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-1">{t('receipt.amount')}</p>
          <p className="text-4xl font-extrabold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {formatCurrency(txn.amount, txn.currency)}
          </p>
          <Badge
            variant="outline"
            className={`mt-3 text-xs ${
              txn.payment_status === 'paid' ? 'status-paid' :
              txn.payment_status === 'refunded' ? 'status-refunded' :
              'status-pending'
            }`}
          >
            {txn.payment_status.toUpperCase()}
          </Badge>
        </div>

        <Separator />

        {/* Details */}
        <div className="p-6 space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-500">{t('receipt.date')}</span>
            <span className="font-medium">{formatDate(txn.created_at)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">{t('receipt.transactionId')}</span>
            <span className="font-mono text-xs">{txn.transaction_id}</span>
          </div>
          {txn.description && (
            <div className="flex justify-between">
              <span className="text-slate-500">{t('receipt.description')}</span>
              <span className="font-medium">{txn.description}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-slate-500">{t('receipt.paidBy')}</span>
            <span className="font-medium">{txn.created_by_name || 'Staff'}</span>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 text-center border-t border-dashed border-border bg-muted">
          <p className="text-xs text-muted-foreground">{store?.receipt_footer || t('receipt.thankYou')}</p>
          <p className="text-xs text-muted-foreground/50 mt-1">{t('receipt.poweredBy')}</p>
        </div>
      </div>
    </div>
  );
}
